import type { Ref } from 'vue'
import type { VerificationResponse } from '@/interfaces/verification'
import { useAxios } from '@/services/composables'

// In-memory cache to store verification data by hash
const verificationCache = new Map<string, VerificationResponse>()

/**
 * Composable for verification API operations with in-memory caching.
 * Cache is automatically managed using hash as the key.
 * Different hashes will automatically fetch new data (cache is hash-specific).
 * @returns {Object} Object with fetchVerification method
 */
export const useVerification = () => {
  const { execute, data, status } = useAxios<VerificationResponse>()

  /**
   * Fetches verification data by QR hash with automatic caching.
   * Checks cache first (using hash as key), then makes network call if not found.
   * If hash is different, automatically fetches new data (cache is hash-specific).
   * Can also cache provided data if not already cached.
   * 
   * @param hash - The verification hash identifier (used as cache key)
   * @param verificationRef - Optional ref to update with verification data
   * @param isLoadingRef - Optional ref to update loading state
   * @param dataToCache - Optional data to cache if not already in cache
   * @returns Promise with verification data
   */
  const fetchVerification = async (
    hash: string,
    verificationRef?: Ref<VerificationResponse | null>,
    isLoadingRef?: Ref<boolean>,
    dataToCache?: VerificationResponse
  ): Promise<VerificationResponse | null> => {
    // If data is provided to cache and not already in cache, cache it first
    if (dataToCache && !verificationCache.has(hash)) {
      verificationCache.set(hash, dataToCache)
      if (verificationRef) {
        verificationRef.value = dataToCache
      }
      return dataToCache
    }

    // Check cache first (using hash as key)
    // Different hash = different cache entry, so it will fetch new data automatically
    const cachedData = verificationCache.get(hash)
    if (cachedData) {
      if (verificationRef) {
        verificationRef.value = cachedData
      }
      return cachedData
    }

    // Not in cache, fetch from API
    if (isLoadingRef) {
      isLoadingRef.value = true
    }

    try {
      await execute(`/qr/fetch/${hash}/`, { method: 'GET' })
      const responseData = data.value as VerificationResponse
      const responseStatus = status.value
      
      if (responseStatus === 200 && responseData) {
        // Store in cache using hash as key
        verificationCache.set(hash, responseData)
        
        if (verificationRef) {
          verificationRef.value = responseData
        }
        
        return responseData
      }
      
      return null
    } finally {
      if (isLoadingRef) {
        isLoadingRef.value = false
      }
    }
  }

  return {
    fetchVerification
  }
}
